import {ActionMenu, ActionList, IconButton} from '@primer/react'
import {
  KebabHorizontalIcon,
  AgentIcon,
  AlertIcon,
  CommentDiscussionIcon,
  GearIcon,
  ChevronRightIcon,
  VersionsIcon,
} from '@primer/octicons-react'
import {useState, useRef, useCallback, useMemo} from 'react'
import {CommentsSidePanel} from './CommentsSidePanel'
import {AlertsSidePanel} from './AlertsSidePanel'
import {AgentSessionsSidePanel} from './AgentSessionsSidePanel'
import {DiffViewSettingsMenuContent} from '@github-ui/diff-view-settings'
import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {useCommitSelectorLabel} from '../../hooks/use-commit-selector-label'
import {useAnnotations, useUnresolvedThreadCount} from '../../stores/selectors'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDockedPanelActions} from '../../contexts/DockedPanelContext'
import type {PageLimits} from '../../page-data/payloads/files'
import type {PullRequest} from '../../page-data/payloads/toolbar'
import type {AliveChannels} from '../../page-data/payloads/header'
import type {Repository} from '@github-ui/current-repository'

import styles from './ToolbarMoreMenu.module.css'

export interface ToolbarMoreMenuProps {
  pageLimits: PageLimits
  pullRequest: PullRequest
  aliveChannels: AliveChannels
  hideWhitespace: boolean
  onUpdateWhitespace: (value: boolean) => void
  additionalPreferences?: React.ReactNode
  showCommitSelector: () => void
  commits: Array<{oid: string}>
  isFileTreeExpanded?: boolean
  repository: Repository
  pullRequestNumber: number
}

export function ToolbarMoreMenu({
  pageLimits,
  pullRequest,
  aliveChannels,
  hideWhitespace,
  onUpdateWhitespace,
  additionalPreferences,
  showCommitSelector,
  commits,
  isFileTreeExpanded = true,
  repository,
  pullRequestNumber,
}: ToolbarMoreMenuProps) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const robustSmallScreenSupport = useFeatureFlag('prx_robust_small_screen_support')
  const isCodingAgentLogsInPrEnabled = isFeatureEnabled('coding_agent_logs_in_pr')
  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const {togglePanel} = useDockedPanelActions()
  const commitOids = useMemo(() => commits.map(commit => commit.oid), [commits])
  const commitSelectorLabel = useCommitSelectorLabel(commitOids)

  const [commentsSidePanelIsOpen, setCommentsSidePanelIsOpen] = useState(false)
  const [alertsSidePanelIsOpen, setAlertsSidePanelIsOpen] = useState(false)
  const [agentSessionsIsOpen, setAgentSessionsIsOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const annotations = useAnnotations()
  const diffCommentCount = useUnresolvedThreadCount()

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const totalAnnotationsCount = annotations.length

  const handleOpenComments = useCallback(() => {
    sendPullRequestAnalyticsEvent('comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')
    ensurePreviousActiveDialogIsClosed()
    setCommentsSidePanelIsOpen(true)
    setIsMenuOpen(false)
  }, [sendPullRequestAnalyticsEvent])

  const handleOpenAgentSessions = useCallback(() => {
    sendPullRequestAnalyticsEvent('agent_sessions_side_panel.open', 'AGENT_SESSIONS_MENU_ITEM')
    ensurePreviousActiveDialogIsClosed()
    if (isPrsDockedSidePanelEnabled) {
      togglePanel('agentSessions', menuButtonRef.current)
    } else {
      setAgentSessionsIsOpen(true)
    }
    setIsMenuOpen(false)
  }, [sendPullRequestAnalyticsEvent, isPrsDockedSidePanelEnabled, togglePanel])

  const handleOpenAlerts = useCallback(() => {
    sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
    ensurePreviousActiveDialogIsClosed()
    setAlertsSidePanelIsOpen(true)
    setIsMenuOpen(false)
  }, [sendPullRequestAnalyticsEvent])

  return (
    <>
      <ActionMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <ActionMenu.Anchor>
          <IconButton
            ref={menuButtonRef}
            icon={KebabHorizontalIcon}
            variant="default"
            size="small"
            aria-label="More options"
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end">
          <ActionList>
            <ActionList.Group>
              {isCodingAgentLogsInPrEnabled && (
                <ActionList.Item onSelect={handleOpenAgentSessions}>
                  <ActionList.LeadingVisual>
                    <AgentIcon />
                  </ActionList.LeadingVisual>
                  Agent sessions
                </ActionList.Item>
              )}
              <ActionList.Item onSelect={handleOpenComments}>
                <ActionList.LeadingVisual>
                  <CommentDiscussionIcon />
                </ActionList.LeadingVisual>
                Comments
                {diffCommentCount > 0 && <ActionList.TrailingVisual>{diffCommentCount}</ActionList.TrailingVisual>}
              </ActionList.Item>
              {totalAnnotationsCount > 0 && (
                <ActionList.Item onSelect={handleOpenAlerts}>
                  <ActionList.LeadingVisual>
                    <AlertIcon />
                  </ActionList.LeadingVisual>
                  Alerts
                  <ActionList.TrailingVisual>{totalAnnotationsCount}</ActionList.TrailingVisual>
                </ActionList.Item>
              )}
              {robustSmallScreenSupport ? (
                <ActionList.Item
                  onSelect={() => showCommitSelector()}
                  className={styles[`show-commit-selector-on-${isFileTreeExpanded ? 'small' : 'medium'}`]}
                >
                  <ActionList.LeadingVisual>
                    <VersionsIcon />
                  </ActionList.LeadingVisual>
                  {commitSelectorLabel}
                </ActionList.Item>
              ) : null}
            </ActionList.Group>
            <ActionList.Divider />
            <ActionList.Group>
              <ActionMenu open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
                <ActionMenu.Anchor>
                  <ActionList.Item>
                    <ActionList.LeadingVisual>
                      <GearIcon />
                    </ActionList.LeadingVisual>
                    Settings
                    <ActionList.TrailingVisual>
                      <ChevronRightIcon />
                    </ActionList.TrailingVisual>
                  </ActionList.Item>
                </ActionMenu.Anchor>
                <ActionMenu.Overlay side="outside-bottom" align="end">
                  <ActionList>
                    <DiffViewSettingsMenuContent
                      reloadOnWhitespaceChange
                      onUpdateWhitespace={onUpdateWhitespace}
                      defaultHideWhitespace={hideWhitespace}
                      additionalPreferences={additionalPreferences}
                    />
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>

      <CommentsSidePanel
        isOpen={commentsSidePanelIsOpen}
        pageLimits={pageLimits}
        pathName={pullRequest.pathName}
        pullRequestAuthorLogin={pullRequest.author.login}
        aliveChannels={aliveChannels}
        toggleSidesheetRef={menuButtonRef}
        onClose={() => setCommentsSidePanelIsOpen(false)}
      />

      {totalAnnotationsCount > 0 && (
        <AlertsSidePanel
          annotations={annotations}
          isOpen={alertsSidePanelIsOpen}
          pageLimits={pageLimits}
          pathName={pullRequest.pathName}
          endCommitOid={pullRequest.comparison.headOid}
          returnFocusRef={menuButtonRef}
          onClose={() => setAlertsSidePanelIsOpen(false)}
        />
      )}

      {isCodingAgentLogsInPrEnabled && !isPrsDockedSidePanelEnabled && (
        <AgentSessionsSidePanel
          isOpen={agentSessionsIsOpen}
          repository={repository}
          pullRequestNumber={pullRequestNumber}
          onClose={() => setAgentSessionsIsOpen(false)}
        />
      )}
    </>
  )
}
